import React, {
    ComponentProps,
    FormEvent,
    PropsWithChildren,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import './ConfirmationAdModule.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import SessionStorage from '../../utils/SessionStorage';
import {
    ICardProtectionModalProps,
} from './components/CreditProtectionModal';
import {
    ICardDesignModalProps,
} from './components/CardDesignModal';
import { speedbumpModal, useModal } from '../../providers/Modal/Modal';
import { Icon } from '../../types/shared/Icon';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import { useWidthContext } from '../../providers/Width';
import withAsyncImport from '../../withAsyncImport';
import { getAppData, getProgramData, AcquisitionFlow, getAcquisitionFlow, clearSessionAndRedirectToDataEntryPage } from '../../utils/acquisition-utils';
import { SpeedbumpContext } from '../../providers/Speedbump/Speedbump';
import { getHostFromStringUrl } from '../../utils/cob-utils';
import { IAppApprovedData } from '../ConfirmationPageHero/ConfirmationPageHero';
import { Section, Button } from '../../importCL';
import { useFreezeLifecycleEvent } from '../../hooks/useFreezeLifecycleEvent';
import { useBackForwardCache } from '../../hooks/useBackForwardCache';
import { checkOfferForCreditProtectionEligibility } from '../CreditProtection/CreditProtection';
const CreditProtectionModal = withAsyncImport(() => import('./components/CreditProtectionModal'));
const CardDesignModal = withAsyncImport(() => import('./components/CardDesignModal'));
const AnimatedIcon = withAsyncImport(() => import('./components/AnimatedIcon'));

export type TModalVersion = 'cardProtection'|'cardDesign'|'noModal';
export interface ICardDesignProps {
    cardDesignAnimationIcons?: Icon[];
    cardDesignHeader?: string;
    cardDesignSubHeader?: string;
    cardDesignCtaText?: string;
    cardDesignModalIcon?: Icon;
    cardDesignHeaderModified?: string;
    cardDesignSubHeaderModified?: string;
    cardDesignSuccessText?: string;
    cardDesignModalHeader?: string;
    cardDesignModalSubHeader?: string;
    numberOfCardsLoaded?: number;
    cardDesignModalCtaText?: string;
    cardDesignModalCtaId?: string;
}

export interface ICardProtectionProps {
    cardProtectionAnimationIcons?: Icon[];
    cardProtectionHeader?: string;
    cardProtectionSubHeader?: string;
    cardProtectionHeaderModified?: string;
    cardProtectionSubHeaderModified?: string;
    cardProtectionSuccessText?: string;
    cardProtectionCtaText?: string;
    cardProtectionModalIcon?: Icon;
    cardProtectionModalHeader?: string;
    cardProtectionCheckboxText?: string;
    benefit1?: string;
    benefit2?: string;
    benefit3?: string;
    benefit4?: string;
    firstLinkIcon?: Icon;
    firstLinkText?: string;
    firstLinkUrl?: string;
    secondLinkIcon?: Icon;
    secondLinkText?: string;
    secondLinkUrl?: string;
    bottomDesc?: string;
    initialsText?: string;
    cardProtectionModalCtaText?: string;
    cardProtectionModalCtaId?: string;
}
export interface IConfirmationAdModuleProps extends ICardDesignProps, ICardProtectionProps {
    version?: 'noModal' | 'dynamicAdExperience';
    header?: string;
    subheader?: string;
    ctaText?: string;
    ctaUrl?: string;
    staticIcon?: Icon;  
    defaultDisclosureText?: string;
    creditProtectionDisclosureText?: string;
    cardDesignDisclosureText?: string;
    cardProtectionErrorCode?: string;
    cardDesignErrorCode?: string;
    errorStaticImage?: string;
    errorStaticImageAlt?: string;
    imageWidth?: string;
    imageHeight?: string;
    mobileImageWidth?: string;
    mobileImageHeight?: string;
    noModalCtaId?: string;
    cardProtectionOpenModalCtaId?: string;
    cardDesignOpenModalCtaId?: string;
}

export interface IAdContent {
    header: string;
    subheader: string;
    successText: string;
    ctaText: string;
    ctaId?: string;
}

export const ConfirmationAdModuleEditConfig = {
    emptyLabel: 'Confirmation Ad Module',
    isEmpty: (props: PropsWithChildren<IConfirmationAdModuleProps>) => {
        return !props || !props.version;
    },
};

const ConfirmationAdModule = (
    props: PropsWithChildren<IConfirmationAdModuleProps>,
): JSX.Element => {
    const { showPushdown } = usePushdownContext();
    const { title, content, whitelist } = useContext(SpeedbumpContext);
    const { toggleModal, setModalOpen, isOpen, setModalContent, setModalClass, setExitAnimationDuration, setExitAnimationClass } = useModal();
    const { width } = useWidthContext();

    const textContainerRef = useRef<HTMLDivElement>(null);

    const [modalVersion, setModalVersion] = useState<TModalVersion>('noModal');
    const [isModalSubmitSuccess, setIsModalSubmitSuccess] =  useState<boolean>();
    const [isModalSubmitError, setIsModalSubmitError] = useState(false);
    const [isCardSecure, setIsCardSecure] = useState(false);
    const [adContent, setAdContent] = useState<IAdContent>({
        header: props.header as string,
        subheader: props.subheader as string,
        successText: '',
        ctaText: props.ctaText as string,
        ctaId: '',
    });
    const approvedData = useRef<IAppApprovedData>({});
    const staticImageStyles = {
        width: `${width > 767 ? props.imageWidth : props.mobileImageWidth}px`,
        height: `${width > 767 ? props.imageHeight : props.mobileImageHeight}px`,
    };
    const cardDesignModalProps: ICardDesignModalProps = {
        cardDesignModalIcon: props.cardDesignModalIcon,
        cardDesignModalHeader: props.cardDesignModalHeader,
        cardDesignModalSubHeader: props.cardDesignModalSubHeader,
        numberOfCardsLoaded: props.numberOfCardsLoaded,
        cardDesignModalCtaText: props.cardDesignModalCtaText,
        cardDesignModalCtaId: props.cardDesignModalCtaId,
    };

    const creditProtectionModalProps: ICardProtectionModalProps = {
        cardProtectionModalIcon: props.cardProtectionModalIcon,
        cardProtectionModalHeader: props.cardProtectionModalHeader,
        cardProtectionCheckboxText: props.cardProtectionCheckboxText,
        benefit1: props.benefit1,
        benefit2: props.benefit2,
        benefit3: props.benefit3,
        benefit4: props.benefit4,
        firstLinkIcon: props.firstLinkIcon,
        firstLinkText: props.firstLinkText,
        firstLinkUrl: props.firstLinkUrl,
        secondLinkIcon: props.secondLinkIcon,
        secondLinkText: props.secondLinkText,
        secondLinkUrl: props.secondLinkUrl,
        bottomDesc: props.bottomDesc,
        initialsText: props.initialsText,
        cardProtectionModalCtaText: props.cardProtectionModalCtaText,
        cardProtectionModalCtaId: props.cardProtectionModalCtaId,
    };

    const animatedIconProps: ComponentProps<typeof AnimatedIcon> = {
        animatedIcons: props[`${modalVersion as 'cardProtection' | 'cardDesign' }AnimationIcons`],
        isSubmitSuccess: isModalSubmitSuccess,
        successIcon: props[`${modalVersion as 'cardProtection' | 'cardDesign' }ModalIcon`],
    };

    useFreezeLifecycleEvent(() => {
        sessionStorage.clear();
        window.location.reload();
    });

    const backForwardCacheCallback = (event: PageTransitionEvent) => {
        if ( // Page loaded from bfcache, browser implementations vary
            event?.persisted
            || window.performance?.navigation.type === window.performance?.navigation.TYPE_BACK_FORWARD
            || (window.performance?.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'back_forward'
            || (window.performance?.getEntriesByType('navigation')[0] as PerformanceNavigationTiming & { notRestoredReasons: unknown })?.notRestoredReasons // Varying support
        ) { // Warning, the above is unreliable
            clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Page loaded from bfcache');
        }
    };
    useBackForwardCache(backForwardCacheCallback, backForwardCacheCallback);

    const callPushdown = (errorCode?: string) => {
        showPushdown(errorCode && errorCode !== 'undefined' ? errorCode : 'ancillaryProcessingError');
    };

    const successCallback = (): void => {
        if (!SessionStorage.verify('confirmationAdModuleSuccessVersion')) {
            SessionStorage.set('confirmationAdModuleSuccessVersion', modalVersion);
        }

        setIsModalSubmitSuccess(true);
    };

    const errorCallback = (errorCode?: string, status?: number) => {
        const defaultErrorKey = `${modalVersion}ErrorCode` as keyof IConfirmationAdModuleProps;
        const errorCodeValue: string = (errorCode || props[defaultErrorKey] || '') as string;

        setIsModalSubmitError(true);
        SessionStorage.set('hasError', errorCodeValue);

        if (status === 408 || errorCode === 'SessionExpired') {
            callPushdown((props[defaultErrorKey] || '') as string);
        } else {
            callPushdown(errorCodeValue);
        }

        console.error(`Error: ${errorCodeValue}`);
    };

    const getCardProtectionFlowData = async (): Promise<{
        isEnrolledInCreditProtection: boolean,
        creditProtectionEligibility: boolean,
    }> => {
        const appData = await getAppData();
        const isSecuredCard = appData?.IsSecuredCard || appData?.card?.keyCardFeatures?.cardType === 'SECURED_CARD' || false;
        const creditProtectionEligibility = await checkOfferForCreditProtectionEligibility();
        if (getAcquisitionFlow() === AcquisitionFlow.PQ && isSecuredCard) {
            return {
                isEnrolledInCreditProtection: SessionStorage.tryGetObject('flowApplicationData', {})?.enrollments?.creditProtectionEnrolled ?? false,
                creditProtectionEligibility: creditProtectionEligibility,
            };
        }

        return {
            isEnrolledInCreditProtection: approvedData?.current?.AdditionalInformation?.IsCreditProtectionSelected ?? false,
            creditProtectionEligibility: creditProtectionEligibility,
        };
    };

    const getCardDesignFlowData = async () => {
        const applicationFlowData = SessionStorage.tryGetObject('flowApplicationData', null);
        // if flowApplicationData and prequalFullAppData both exist
        if (getAcquisitionFlow() === AcquisitionFlow.PQ && applicationFlowData) {
            setIsCardSecure(applicationFlowData?.card?.keyCardFeatures?.cardType === 'SECURED_CARD' || 
                applicationFlowData?.card?.keyCardFeatures?.cardType ==='SECURED_CARDS');
            const selectedCard = applicationFlowData?.card?.keyCardFeatures?.cardVariations?.[0];
            const isPremiumCardAvailable = applicationFlowData?.card?.keyCardFeatures?.cardVariations?.some((card) => card.premiumFee && card.premiumFee > 0);
            const isSelectedCardFree = selectedCard?.premiumFee && selectedCard?.premiumFee > 0;
            return { selectedCard, isPremiumCardAvailable, isSelectedCardFree };
        }
        const appData = await getAppData();
        const programData = await getProgramData();
        if (appData.IsSecuredCard) {
            setIsCardSecure(true);
        }
        const selectedCard = appData.SelectedCardId ? parseInt(appData.SelectedCardId) : parseInt(appData.SelectedProgramPlasticCode);
        const isPremiumCardAvailable = programData?.CardDesign?.Cards?.some((card: any) => card.IsPremium && card.PremiumFee > 0) ?? false;
        const isSelectedCardFree = programData?.CardDesign?.Cards?.some((card: any) => {
            return parseInt(card?.ProgramPlasticCode) === selectedCard && (!card.IsPremium || card.PremiumFee <= 0);
        }) ?? true;

        return { selectedCard, isPremiumCardAvailable, isSelectedCardFree };
    };

    // waterfall to show the correct modal for dynamicAdExperience
    // otherwise, default click behavior
    const handleOnClick = async (e: FormEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!props.ctaUrl) {
            console.error('Missing redirect URL');
            return;
        }

        if (!modalVersion || modalVersion === 'noModal') {
            const isWhitelisted = whitelist && whitelist.some((entry) => props.ctaUrl?.includes(entry));
            const isRelativePathOrAnchor = ['#', '/', './'].some(entry => props.ctaUrl?.startsWith(entry));
            const isSameHost = getHostFromStringUrl(props.ctaUrl) === window.location.host;

            if (isWhitelisted || isRelativePathOrAnchor || isSameHost) {
                window.location.assign(props.ctaUrl);
                return;
            }

            setModalContent(speedbumpModal(toggleModal, width, undefined, props.ctaUrl, title, content));
            setModalClass('Speedbump');
        } else {
            const { isPremiumCardAvailable, isSelectedCardFree } = await getCardDesignFlowData();
            const cardProtectionFlowData = await getCardProtectionFlowData();
            if (cardProtectionFlowData.creditProtectionEligibility && !cardProtectionFlowData.isEnrolledInCreditProtection) {
                setModalContent(
                    <CreditProtectionModal {...creditProtectionModalProps}
                    successCallback={successCallback}
                    closeCallback={() => setModalOpen(false)}
                    errorCallback={errorCallback} />,
                );
                setModalClass('CreditProtectionModal');
            } else if (isSelectedCardFree && isPremiumCardAvailable) {
                setModalContent(
                    <CardDesignModal {...cardDesignModalProps}
                    successCallback={successCallback}
                    closeCallback={() => setModalOpen(false)}
                    errorCallback={errorCallback} />,
                );
                setModalClass('CardDesignModal');
            } else {
                window.location.assign(props.ctaUrl);
                return;
            }
            setExitAnimationDuration(400);
            setExitAnimationClass('exit');
        }
        setModalOpen(true);  
    };

    useEffect(() => {
        if (props.version === 'dynamicAdExperience') {
            getCardDesignFlowData().then(async ({ isPremiumCardAvailable, isSelectedCardFree }) => {
                const { isEnrolledInCreditProtection, creditProtectionEligibility } = await getCardProtectionFlowData();

                if (SessionStorage.verify('confirmationAdModuleSuccessVersion')) {
                    setModalVersion(SessionStorage.get('confirmationAdModuleSuccessVersion') as TModalVersion);
                    successCallback();
                } else if (creditProtectionEligibility && !isEnrolledInCreditProtection) {
                    setModalVersion('cardProtection');
                } else if (isSelectedCardFree && isPremiumCardAvailable) {
                    setModalVersion('cardDesign');
                } else {
                    setModalVersion('noModal');
                }
                });
        } else {
        setModalVersion('noModal');
        }

        if (SessionStorage.verifyNotEmpty('hasError')) {
            setIsModalSubmitError(true);
            callPushdown(SessionStorage.get('hasError') as string);
        }
        const getAppData = async () => {
            const data = await SessionStorage.tryGetCipher('app-approved-data', {});
            approvedData.current = data;
        };
        getAppData();
    }, []);

    // recalculate container width and update ad content whenever modal version changes
    useEffect(() => {
        // if noModal, always show default content
        if (modalVersion === 'noModal') {
            setAdContent({
                header: props.header as string,
                subheader: props.subheader as string,
                successText: '',
                ctaText: props.ctaText as string,
                ctaId: props.noModalCtaId as string,
            });
        } else {
            // if not submitted, show initial content for CP or CD
            // if submitted, show modified content for CP or CD
            const headerKey = `${modalVersion}Header${isModalSubmitSuccess ? 'Modified' : ''}` as keyof IConfirmationAdModuleProps;
            const subHeaderKey = `${modalVersion}SubHeader${isModalSubmitSuccess ? 'Modified' : ''}` as keyof IConfirmationAdModuleProps;
            // only if submit success
            const successTextKey = `${modalVersion}SuccessText` as keyof IConfirmationAdModuleProps;
            // CTA is not displayed after submit
            const ctaTextKey = `${modalVersion}CtaText` as keyof IConfirmationAdModuleProps;
            
            const ctaOpenModalId = `${modalVersion}OpenModalCtaId` as keyof IConfirmationAdModuleProps;
            
            setAdContent({
                header: props[headerKey] as string,
                subheader: props[subHeaderKey] as string,
                successText: props[successTextKey] as string,
                ctaText: props[ctaTextKey] as string,
                ctaId: props[ctaOpenModalId] as string,
            });
        }
    }, [modalVersion, isModalSubmitSuccess]);

    useEffect(() => {
        if (textContainerRef.current && textContainerRef.current.textContent) {
            const textLength = textContainerRef.current.textContent.length;
            if (textLength > 72) {
                textContainerRef.current.style.width = '95%';
            } else {
                textContainerRef.current.style.width = '75%';
            }
        }
    }, [adContent]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [isOpen]);

    return (
        <div id='confirmation-ad-module' className='ConfirmationAdModule'>
            <Section className={`ConfirmationAdModule__section ${isModalSubmitError ? 'ConfirmationAdModule__section--error' : ''}`}
                containerClass='ConfirmationAdModule__sectionContainer'>
                {(isModalSubmitError || isCardSecure) && (
                    <div className='ConfirmationAdModule__staticErrorImage'>
                        <img loading='lazy' alt={props.errorStaticImageAlt} src={props.errorStaticImage} style={staticImageStyles} />
                    </div>
                )}
                {!isModalSubmitError && !isCardSecure && (
                    <>
                        <div className='ConfirmationAdModule__header'>
                            {adContent.header && transformHTMLParserLinks(adContent.header)}
                        </div>
                        <div className='ConfirmationAdModule__mainContainer'>
                            <div className='ConfirmationAdModule__subContainer'>
                                <div className='ConfirmationAdModule__imgContainer'>
                                    {modalVersion === 'noModal' && (
                                        <img className='ConfirmationAdModule__img'
                                        loading='lazy'
                                        src={props.staticIcon?.iconPath}
                                        alt={props.staticIcon?.iconAltText} />
                                    )}
                                    {modalVersion !== 'noModal' && (
                                        <AnimatedIcon {...animatedIconProps} />
                                    )}
                                </div>
                                <div className='ConfirmationAdModule__textContainer'>
                                    <div className='ConfirmationAdModule__text' ref={textContainerRef}>
                                        {adContent.subheader && transformHTMLParserLinks(adContent.subheader)}
                                    </div>
                                    {isModalSubmitSuccess ? (
                                            <p className='ConfirmationAdModule__successText'>
                                                {transformHTMLParserLinks(adContent.successText)}
                                            </p>
                                        ) : (
                                            <Button className='ConfirmationAdModule__btn'
                                                id={adContent.ctaId || undefined}
                                                size='small'
                                                variant='ghosted-dark'
                                                onClick={(e: FormEvent<HTMLButtonElement>) => {
                                                    handleOnClick(e).catch(err => console.error(err)); 
                                                }}>
                                                {adContent.ctaText}
                                            </Button>
                                        )
                                    }
                                </div>
                            </div>
                            <div>
                                {props.defaultDisclosureText && modalVersion ===  'noModal' ? transformHTMLParserLinks(props.defaultDisclosureText) : ''}
                                {props.creditProtectionDisclosureText && modalVersion === 'cardProtection' ? transformHTMLParserLinks(props.creditProtectionDisclosureText) : ''}
                                {props.cardDesignDisclosureText && modalVersion === 'cardDesign' ? transformHTMLParserLinks(props.cardDesignDisclosureText) : ''}
                            </div>
                        </div>
                    </>
                )}
            </Section>
        </div>
    );
};

export default ConfirmationAdModule;
